/**
 * Ghost Rider — „Marvel: Redemption Row".
 *
 * HELLFIRE je verejné počítadlo `counters.hellfire` (začína na 5, strop nemá).
 * Pri manévri smie minúť 1 Hellfire: pohyb 4 ponad súperov a 1 poškodenie
 * každému, cez koho prešiel (`HeroSpecial.maneuverStart` → presun s
 * pokračovaním `special`, ktoré dostane cestu).
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, drawCard, emit, gainAction, getMap } from '../ops.js';
import { areEnemies } from '../players.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { feint } from './common.js';
import { addCounter, askMove, canMove, counter, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const RIDER = 'ghost-rider';
const HELLFIRE = 'hellfire';
const ESCAPED = 'ghost-rider.i-finally-escaped-hell';
const VENGEANCE = 'ghost-rider.spirit-of-vengeance';
const DEVIL = 'ghost-rider.i-brought-the-devil-with-me';
const BLAZE = 'ghost-rider.blaze-of-glory';
const DEAL = 'ghost-rider.deal-with-the-devil';
const STARE = 'ghost-rider.penance-stare';
const RIDES = 'ghost-rider.hell-rides-with-me';

const riderOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === RIDER) ?? null;

const hellfire = (ctx: Ctx, player: PlayerIndex) => counter(ctx, player, HELLFIRE);

/** Minie `n` Hellfire, ak ich toľko má. */
function spend(ctx: Ctx, player: PlayerIndex, n: number): boolean {
  if (n <= 0 || hellfire(ctx, player) < n) return false;
  addCounter(ctx, player, HELLFIRE, -n);
  return true;
}

/** „You may spend N Hellfire to …" — voľba `confirm` s pokračovaním karty. */
function offerSpend(e: EffectCtx, n: number, effect: string, label: string, yes: string): void {
  if (hellfire(e.ctx, e.player) < n) return;
  askChoice(e.ctx, {
    kind: 'option',
    player: e.player,
    source: e.cardId,
    label,
    optional: true,
    options: [{ label: yes, follow: { kind: 'effect', effect, data: String(n) } }],
  });
}

/** Voľba „minúť ľubovoľne veľa Hellfire" (0…všetky) — ako Beowulfov Rage. */
function askAmount(e: EffectCtx, effect: string, label: string, extra = ''): void {
  const have = hellfire(e.ctx, e.player);
  if (have === 0) return;
  askChoice(e.ctx, {
    kind: 'option',
    player: e.player,
    source: e.cardId,
    label,
    optional: true,
    options: Array.from({ length: have }, (_, i) => ({
      label: `Minúť ${i + 1}`,
      follow: { kind: 'effect' as const, effect, data: `${i + 1}${extra}` },
    })),
  });
}

/** „You may spend 1 Hellfire to move Ghost Rider up to 2 spaces." */
const iFinallyEscapedHell: CardEffect = {
  after: (e) => {
    const rider = riderOf(e.ctx, e.player);
    if (rider && canMove(e.ctx, rider.id, 2, false)) {
      offerSpend(e, 1, ESCAPED, 'I Finally Escaped Hell: minúť 1 Hellfire na presun o 2?', 'Minúť 1 a presunúť sa');
    }
  },
  onFollow: (ctx, choice) => {
    const rider = riderOf(ctx, choice.player);
    if (!rider || !spend(ctx, choice.player, 1)) return;
    askChoice(ctx, {
      kind: 'move',
      player: choice.player,
      source: choice.source,
      label: 'I Finally Escaped Hell: presuň Ghost Ridera až o 2',
      optional: true,
      fighters: [rider.id],
      max: 2,
      through: false,
    });
  },
};

/** „You may spend 1 Hellfire to draw 2 cards." */
const spiritOfVengeance: CardEffect = {
  after: (e) => offerSpend(e, 1, VENGEANCE, 'Spirit of Vengeance: minúť 1 Hellfire na 2 karty?', 'Minúť 1 a potiahnuť 2'),
  onFollow: (ctx, choice) => {
    if (!spend(ctx, choice.player, 1)) return;
    drawCard(ctx, choice.player);
    drawCard(ctx, choice.player);
  },
};

/** „You may spend 2 Hellfire to gain 1 action." */
const iBroughtTheDevil: CardEffect = {
  after: (e) => {
    if (e.ctx.state.turn.player !== e.player) return;
    offerSpend(e, 2, DEVIL, 'I Brought the Devil With Me: minúť 2 Hellfire na akciu?', 'Minúť 2 a získať akciu');
  },
  onFollow: (ctx, choice) => {
    if (spend(ctx, choice.player, 2)) gainAction(ctx, choice.player);
  },
};

/** „Spend any amount of Hellfire. Deal that much damage to each fighter in Ghost Rider's zone (including Ghost Rider)." */
const blazeOfGlory: CardEffect = {
  after: (e) => askAmount(e, BLAZE, 'Blaze of Glory: koľko Hellfire minúť? Toľko poškodenia dostane každý v zóne — aj Ghost Rider.'),
  onFollow: (ctx, choice, data) => {
    const n = Number(data);
    const rider = riderOf(ctx, choice.player);
    if (!rider || !spend(ctx, choice.player, n)) return;
    const map = getMap(ctx);
    const inZone = ctx.state.fighters
      .filter((f) => f.id === rider.id || shareZone(map, f.spaceId, rider.spaceId))
      .map((f) => f.id);
    for (const id of inZone) dealDamage(ctx, id, n);
  },
};

/** „If you lost the combat, gain Hellfire equal to the combat damage you took." */
const stokeTheFlames: CardEffect = {
  after: (e) => {
    if (!e.won && e.damage) addCounter(e.ctx, e.player, HELLFIRE, e.damage);
  },
};

const side = (e: EffectCtx) => (e.role === 'defender' ? 'defender' : 'attacker');

/** „DURING: You may spend any amount of Hellfire. Increase the value of this card by that amount." */
const dealWithTheDevil: CardEffect = {
  duringChoice: (e) => askAmount(e, DEAL, 'Deal with the Devil: koľko Hellfire minúť? Toľko pridáš k hodnote.', `:${side(e)}`),
  onFollow: (ctx, choice, data) => {
    const [n, s] = data.split(':') as [string, 'attacker' | 'defender'];
    const combat = ctx.state.combat;
    if (!combat || !spend(ctx, choice.player, Number(n))) return;
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[s] += Number(n);
  },
};

/** „This card's value is +1 for each Hellfire you have. (You do not spend Hellfire for this effect.)" */
const controlTheDemon: CardEffect = {
  during: (e, value) => value + hellfire(e.ctx, e.player),
};

/**
 * „DURING: Add the BOOST value of the opposing fighter's card to this card's
 * value. You may spend 2 Hellfire to do it a second time."
 */
const penanceStare: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const theirs = e.role === 'defender' ? (combat?.bonus ? null : combat?.attackCard) : combat?.defenseCard;
    if (!theirs) return;
    offerSpend(e, 2, STARE, 'Penance Stare: minúť 2 Hellfire a pridať súperov BOOST ešte raz?', 'Minúť 2 — druhýkrát');
  },
  onFollow: (ctx, choice) => {
    const combat = ctx.state.combat;
    if (combat && spend(ctx, choice.player, 2)) (combat.flags ??= []).push(`stare:${choice.player}`);
  },
  during: (e, value) => {
    const combat = e.ctx.state.combat;
    const theirs = e.role === 'defender' ? (combat?.bonus ? null : combat?.attackCard) : combat?.defenseCard;
    if (!theirs) return value;
    const boost = cardDef(e.ctx, theirs).boost;
    const twice = combat?.flags?.includes(`stare:${e.player}`);
    return value + boost * (twice ? 2 : 1);
  },
};

/** „If Ghost Rider started this turn in a different space, gain 2 Hellfire." */
const theWickedWillBurn: CardEffect = {
  after: (e) => {
    const rider = riderOf(e.ctx, e.player);
    const start = rider ? e.ctx.state.turn.startSpaces[rider.id] : undefined;
    if (rider && start && start !== rider.spaceId) addCounter(e.ctx, e.player, HELLFIRE, 2);
  },
};

/**
 * „Move Ghost Rider and up to one adjacent fighter up to 4 spaces each. Gain
 * 2 Hellfire. Gain 1 action." Spolujazdec sa vyberá zo susedov pred jazdou.
 */
const hellRidesWithMe: CardEffect = {
  scheme: (e) => {
    const rider = riderOf(e.ctx, e.player);
    if (rider) {
      const around = new Set(neighbors(getMap(e.ctx), rider.spaceId));
      const passengers: FighterId[] = e.ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
      askMove(e, { fighters: [rider.id], max: 4, label: 'Hell Rides With Me: presuň Ghost Ridera až o 4' });
      if (passengers.length > 0) {
        askChoice(e.ctx, {
          kind: 'option',
          player: e.player,
          source: e.cardId,
          label: 'Hell Rides With Me: vezmeš so sebou susednú figúrku (až o 4)?',
          optional: true,
          options: passengers.map((id) => ({
            label: fighterOrNull(e.ctx, id)?.name ?? id,
            follow: { kind: 'effect' as const, effect: RIDES, data: id },
          })),
        });
      }
    }
    addCounter(e.ctx, e.player, HELLFIRE, 2);
    gainAction(e.ctx, e.player);
  },
  onFollow: (ctx, choice, id) => {
    if (!canMove(ctx, id, 4, false)) return;
    askChoice(ctx, {
      kind: 'move',
      player: choice.player,
      source: choice.source,
      label: `Hell Rides With Me: presuň ${fighterOrNull(ctx, id)?.name ?? 'figúrku'} až o 4`,
      optional: true,
      fighters: [id],
      max: 4,
      through: false,
    });
  },
};

/** „Set your Hellfire to 5." */
const chainsOfHellfire: CardEffect = {
  scheme: (e) => void addCounter(e.ctx, e.player, HELLFIRE, 5 - hellfire(e.ctx, e.player)),
};

export const ghostRiderCards: EffectRegistry = {
  [ESCAPED]: iFinallyEscapedHell,
  [VENGEANCE]: spiritOfVengeance,
  [DEVIL]: iBroughtTheDevil,
  [BLAZE]: blazeOfGlory,
  'ghost-rider.stoke-the-flames': stokeTheFlames,
  [DEAL]: dealWithTheDevil,
  'ghost-rider.feint': feint,
  'ghost-rider.control-the-demon': controlTheDemon,
  [STARE]: penanceStare,
  'ghost-rider.the-wicked-will-burn': theWickedWillBurn,
  [RIDES]: hellRidesWithMe,
  'ghost-rider.chains-of-hellfire': chainsOfHellfire,
};

export const ghostRiderSpecial: HeroSpecial = {
  name: 'Hellfire',
  /** „Ghost Rider starts the game with 5 Hellfire." */
  setup: (ctx, player) => {
    const seat = ctx.state.players[player];
    if (seat) (seat.counters ??= {})[HELLFIRE] = 5;
  },
  /**
   * „When you maneuver you may spend 1 Hellfire. If you do, increase Ghost
   * Rider's move value to 4, and he may move through opposing fighters. Then
   * deal 1 damage to each opposing fighter he moved through."
   */
  maneuverStart: (ctx, player) => {
    const rider = riderOf(ctx, player);
    if (!rider || hellfire(ctx, player) < 1 || !canMove(ctx, rider.id, 4, true)) return;
    askChoice(ctx, {
      kind: 'confirm',
      player,
      source: null,
      label: 'Hellfire: minúť 1 na jazdu — pohyb 4 ponad súperov, 1 poškodenie každému, cez koho prejdeš?',
      optional: true,
      special: RIDER,
      yes: 'Minúť 1 Hellfire',
      no: 'Nie, bežný manéver',
    });
  },
  onConfirm: (ctx, player) => {
    const rider = riderOf(ctx, player);
    const maneuver = ctx.state.maneuver;
    if (!rider || !spend(ctx, player, 1)) return;
    // Jazda je jeho pohyb v tomto manévri — druhýkrát sa už nepohne.
    if (maneuver && !maneuver.movedFighters.includes(rider.id)) maneuver.movedFighters.push(rider.id);
    emit(ctx, { type: 'specialUsed', player, heroId: RIDER, name: 'Hellfire: jazda' });
    askChoice(ctx, {
      kind: 'move',
      player,
      source: null,
      label: 'Hellfire: presuň Ghost Ridera až o 4 (smie ponad súperov)',
      optional: true,
      fighters: [rider.id],
      max: 4,
      through: true,
      follow: { kind: 'special', hero: RIDER, data: 'ride' },
    });
  },
  onFollow: (ctx, player, _data, context) => {
    const passed = new Set((context?.path ?? []).slice(1, -1));
    const burned = ctx.state.fighters
      .filter((f) => passed.has(f.spaceId) && areEnemies(ctx.state, f.owner, player))
      .map((f) => f.id);
    for (const id of burned) dealDamage(ctx, id, 1);
  },
};
